import { get, post } from "@/services/axiosUitils";
import {
  ASSESSMENT_STATUS_CONFIG,
  getStatusEndpoints,
} from "../config/assessmentStatusConfig";
import {
  parseStatusResponse,
  validateStatusResponse,
  createParsingErrorResponse,
} from "../utils/responseParser";
import { StatusCache } from "../utils/statusCache";
import type {
  AssessmentCompletionResponse,
  StatusCheckOptions,
  MethodAttemptResult,
} from "../types/assessmentStatusTypes";

// Re-export types for backward compatibility
export { type AssessmentCompletionResponse } from "../types/assessmentStatusTypes";

/**
 * Assessment Status Service Class
 * Handles assessment completion status checks with caching
 */
class AssessmentStatusService {
  /**
   * Try HTTP method with error handling
   */
  private async tryHttpMethod(
    method: "GET" | "POST",
    endpoint: string
  ): Promise<MethodAttemptResult> {
    try {
      // ✅ Using common utilities - Token & Tenant-ID auto-injected!
      const response =
        method === "GET"
          ? await get<any>(endpoint)
          : await post<any>(endpoint, {});

      return {
        success: true,
        response: response,
        method,
      };
    } catch (error) {
      return {
        success: false,
        error,
        method,
      };
    }
  }

  /**
   * Process successful API response
   */
  private processSuccessfulResponse(
    responseData: any,
    token: string
  ): AssessmentCompletionResponse {
    // Validate response structure
    if (!validateStatusResponse(responseData)) {
      return createParsingErrorResponse();
    }

    // Parse the response
    const parsedResponse = parseStatusResponse(responseData);

    // Cache the result
    StatusCache.set(token, parsedResponse);

    return parsedResponse;
  }

  /**
   * Check assessment completion status
   * Uses common get/post utilities - Token & Tenant-ID auto-injected!
   */
  checkAssessmentCompletion = async (
    token: string,
    options: StatusCheckOptions = {}
  ): Promise<AssessmentCompletionResponse> => {
    try {
      // Check cache first (unless force refresh)
      if (options.useCache !== false && !options.forceRefresh) {
        const cached = StatusCache.get(token);
        if (cached) {
          return cached;
        }
      }

      const endpoints = getStatusEndpoints();
      const maxRetries = ASSESSMENT_STATUS_CONFIG.RETRY_COUNT;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          // Try GET method first (most common)
          const getResult = await this.tryHttpMethod(
            "GET",
            endpoints.checkCompletion
          );

          if (getResult.success) {
            const parsedResponse = this.processSuccessfulResponse(
              getResult.response,
              token
            );
            return parsedResponse;
          }

          // If GET fails with 405, try POST
          if ((getResult.error as any)?.response?.status === 405) {
            const postResult = await this.tryHttpMethod(
              "POST",
              endpoints.checkCompletion
            );

            if (postResult.success) {
              const parsedResponse = this.processSuccessfulResponse(
                postResult.response,
                token
              );
              return parsedResponse;
            }

            // If both methods fail, handle error
            const error = postResult.error ?? getResult.error;

            if (attempt === maxRetries) {
              throw error;
            }

            // Wait before retry
            await new Promise(resolve =>
              setTimeout(
                resolve,
                ASSESSMENT_STATUS_CONFIG.RETRY_DELAY_BASE * attempt
              )
            );
          } else {
            // Handle other GET errors
            if (attempt === maxRetries) {
              throw getResult.error;
            }

            await new Promise(resolve =>
              setTimeout(
                resolve,
                ASSESSMENT_STATUS_CONFIG.RETRY_DELAY_BASE * attempt
              )
            );
          }
        } catch (error) {
          if (attempt === maxRetries) {
            throw error;
          }
        }
      }

      throw new Error("Assessment status check failed after all retries");
    } catch (error: any) {
      // Error already handled by axiosUitils handelError()

      // Return safe default for NOT FOUND errors
      if (error?.response?.status === 404) {
        return {
          isComplete: false,
          message: "No assessment found - needs to be completed",
          confidence: 0.8,
          source: "error_handling",
        };
      }

      throw new Error(error.message ?? "Failed to check assessment status");
    }
  };

  /**
   * Clear status cache for user logout
   */
  clearCache = (token?: string): void => {
    if (token) {
      StatusCache.clear(token);
    } else {
      StatusCache.clearAll();
    }
  };

  /**
   * Get cache statistics for monitoring
   */
  getCacheStats = () => {
    return StatusCache.getStats();
  };

  /**
   * Force cleanup of expired cache entries
   */
  forceCleanupCache = (): void => {
    StatusCache.cleanup();
  };
}

// Export singleton instance (City Collab pattern)
const assessmentStatusService = new AssessmentStatusService();
export default assessmentStatusService;
